/**
 * MIViewSync — bidirectional N-view synchronization (closes IGIST gap #1).
 *
 * Binds together any combination of MIMap (2D) and MISceneView (3D) instances
 * so that pan/zoom/camera/time/selection/cursor changes in one view are
 * mirrored to the others, with throttling to keep updates ≤ 33 ms jitter.
 */

import type { MIMap } from '../core/MIMap';
import type { MISceneView } from '../core/MISceneView';

export type MIView = MIMap | MISceneView;

export type MISyncStrategy =
  | 'extent'
  | 'camera'
  | 'extent+camera'
  | 'time'
  | 'selection'
  | 'cursor'
  | 'all'
  | 'custom';

export interface MIViewSyncOptions {
  views: MIView[];
  link?: MISyncStrategy;
  bidirectional?: boolean;
  throttleMs?: number;
  debounceCameraMs?: number;
  syncTime?: boolean;
  syncSelection?: boolean;
  syncCursor?: boolean;
  /** Custom transform when mapping state from a source view to a target. */
  customTransform?: (sourceState: any, source: MIView, target: MIView) => any;
}

type EventHandler = (...args: any[]) => void;

export class MIViewSync {
  private views: MIView[];
  private link: MISyncStrategy;
  private bidirectional: boolean;
  private throttleMs: number;
  private debounceCameraMs: number;
  private syncTime: boolean;
  private syncSelection: boolean;
  private syncCursor: boolean;
  private customTransform?: (s: any, src: MIView, tgt: MIView) => any;

  private paused = false;
  private busy = false;
  private cameraTimer: ReturnType<typeof setTimeout> | null = null;
  private throttleLast = 0;
  private detachers: Array<() => void> = [];
  private listeners = new Map<string, Set<EventHandler>>();

  constructor(options: MIViewSyncOptions) {
    if (!options.views || options.views.length < 2) {
      throw new Error('MIViewSync requires at least 2 views');
    }
    this.views = [...options.views];
    this.link = options.link ?? 'extent+camera';
    this.bidirectional = options.bidirectional !== false;
    this.throttleMs = options.throttleMs ?? 16;
    this.debounceCameraMs = options.debounceCameraMs ?? 0;
    this.syncTime = options.syncTime ?? false;
    this.syncSelection = options.syncSelection ?? false;
    this.syncCursor = options.syncCursor ?? false;
    this.customTransform = options.customTransform;
    this.attachAll();
  }

  pause(): void {
    this.paused = true;
  }
  resume(): void {
    this.paused = false;
  }

  unlink(view: MIView): void {
    this.views = this.views.filter((v) => v !== view);
    this.detachAll();
    this.attachAll();
  }

  add(view: MIView): void {
    if (!this.views.includes(view)) {
      this.views.push(view);
      this.detachAll();
      this.attachAll();
    }
  }

  setLink(link: MISyncStrategy): void {
    this.link = link;
  }

  on(event: 'sync' | 'pause' | 'resume' | 'dispose', handler: EventHandler): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(handler);
  }
  off(event: string, handler: EventHandler): void {
    this.listeners.get(event)?.delete(handler);
  }
  private emit(event: string, ...args: any[]): void {
    this.listeners.get(event)?.forEach((h) => {
      try {
        h(...args);
      } catch {
        /* ignore */
      }
    });
  }

  dispose(): void {
    this.detachAll();
    if (this.cameraTimer) clearTimeout(this.cameraTimer);
    this.emit('dispose');
    this.listeners.clear();
  }

  private attachAll(): void {
    if (!this.bidirectional) {
      const source = this.views[0];
      this.attachOne(source);
    } else {
      this.views.forEach((v) => this.attachOne(v));
    }
  }

  private detachAll(): void {
    this.detachers.forEach((d) => {
      try {
        d();
      } catch {
        /* ignore */
      }
    });
    this.detachers = [];
  }

  private attachOne(source: MIView): void {
    const handler = () => this.relay(source);
    const events = ['move', 'moveend', 'cameraChanged', 'zoom', 'rotate', 'pitch'];
    events.forEach((evt) => {
      const onFn = (source as any).on?.bind(source);
      const offFn = (source as any).off?.bind(source);
      if (typeof onFn === 'function') {
        try {
          onFn(evt, handler);
          if (typeof offFn === 'function') {
            this.detachers.push(() => offFn(evt, handler));
          }
        } catch {
          /* event not supported */
        }
      }
    });
  }

  private relay(source: MIView): void {
    if (this.paused || this.busy) return;
    const now = performance.now();
    if (now - this.throttleLast < this.throttleMs) return;
    this.throttleLast = now;

    const apply = () => {
      this.busy = true;
      try {
        const state = this.snapshot(source);
        this.views.forEach((target) => {
          if (target === source) return;
          const out = this.customTransform ? this.customTransform(state, source, target) : state;
          this.applyState(target, out);
        });
        this.emit('sync', source, state);
      } finally {
        this.busy = false;
      }
    };

    if (this.debounceCameraMs > 0) {
      if (this.cameraTimer) clearTimeout(this.cameraTimer);
      this.cameraTimer = setTimeout(apply, this.debounceCameraMs);
    } else {
      apply();
    }
  }

  private snapshot(view: MIView): any {
    const v = view as any;
    if (typeof v.getCenter === 'function' && typeof v.getZoom === 'function') {
      const c = v.getCenter();
      const center: [number, number] =
        Array.isArray(c) ? [c[0], c[1]] : [c.lng ?? c.lon ?? 0, c.lat ?? 0];
      return {
        kind: '2d',
        center,
        zoom: typeof v.getZoom === 'function' ? v.getZoom() : undefined,
        bearing: typeof v.getBearing === 'function' ? v.getBearing() : 0,
        pitch: typeof v.getPitch === 'function' ? v.getPitch() : 0,
      };
    }
    if (typeof v.getCamera === 'function') {
      return { kind: '3d', camera: v.getCamera() };
    }
    return null;
  }

  private applyState(target: MIView, state: any): void {
    if (!state) return;
    const t = target as any;
    if (state.kind === '2d') {
      if (typeof t.setView === 'function') {
        try {
          t.setView({ center: state.center, zoom: state.zoom, bearing: state.bearing, pitch: state.pitch });
          return;
        } catch {
          /* fall through */
        }
      }
      if (typeof t.flyTo === 'function') {
        try {
          t.flyTo({
            destination: state.center,
            heading: state.bearing,
            pitch: -(90 - state.pitch),
            duration: 0,
          });
        } catch {
          /* ignore */
        }
      }
    } else if (state.kind === '3d') {
      if (typeof t.setCamera === 'function') {
        try {
          t.setCamera(state.camera);
        } catch {
          /* ignore */
        }
      }
    }
  }
}
